export type InvoiceInfo = {
  invoiceMediaId: string;
  invoiceNo: string;
  invoiceAmount: string;
  invoiceDate: string;
};

type KeyMappings = {
  [key in keyof InvoiceInfo]: string;
};

const excludedKeys: Array<keyof InvoiceInfo> = ["invoiceMediaId"];

export function transformInvoiceInfoKeys(
  obj: InvoiceInfo
): Record<string, string> {
  const keyMappings: KeyMappings = {
    invoiceMediaId: "Invoice Media Id",
    invoiceNo: "Invoice Number",
    invoiceAmount: "Invoice Amount",
    invoiceDate: "Invoice Date",
  };

  const transformedObj: Record<string, string> = {};

  for (const key in obj) {
    if (!excludedKeys.includes(key as keyof InvoiceInfo)) {
      if (keyMappings[key as keyof InvoiceInfo]) {
        transformedObj[keyMappings[key as keyof InvoiceInfo]] =
          obj[key as keyof InvoiceInfo];
      } else {
        transformedObj[key] = obj[key as keyof InvoiceInfo];
      }
    }
  }

  return transformedObj;
}

export type OrderItem = {
  partId: string;
  rootPartNumber: string;
  orderedQuantity: number;
  [key: string]: any;
};

export type SupplierParts = {
  rootPartNumber: string;
  familyQuantity: number;
  [key: string]: any;
};

export type OrderItemDiff = {
  fulfilledOrderItems: OrderItem[];
  remainingOrderItems: OrderItem[];
};

export function splitOrderItemsBySupplierInventory(
  orderItems: OrderItem[],
  supplierParts: SupplierParts[]
): OrderItemDiff {
  const supplierInventoryMap = new Map<string, number>();
  supplierParts.forEach((supplierPart) => {
    supplierInventoryMap.set(
      supplierPart.rootPartNumber,
      supplierPart.familyQuantity
    );
  });

  const fulfilledOrderItems: OrderItem[] = [];
  const remainingOrderItems: OrderItem[] = [];

  for (const item of orderItems) {
    const requestedQuantity = item.orderedQuantity;
    const availableQuantity =
      supplierInventoryMap.get(item.rootPartNumber) ?? 0;

    fulfilledOrderItems.push({
      ...item,
      orderedQuantity: Math.min(requestedQuantity, availableQuantity),
    });

    remainingOrderItems.push({
      ...item,
      orderedQuantity: Math.max(0, requestedQuantity - availableQuantity),
    });
  }

  return {
    fulfilledOrderItems: fulfilledOrderItems.filter(
      (item) => item.orderedQuantity !== 0
    ),
    remainingOrderItems: remainingOrderItems.filter(
      (item) => item.orderedQuantity !== 0
    ),
  };
}

export type OrderMetaInfo = {
  orderHandlerAllocationTime: string;
  [key: string]: any;
};

export function calculateStatsFromCustomers(customers: any[]) {
  const totalOrders = customers.reduce(
    (sum: number, customer: any) => sum + (customer.count || 0),
    0
  );
  const totalValues = customers.reduce(
    (sum: number, customer: any) => sum + (customer.totalInvoiceAmount || 0),
    0
  );
  const urgentOrders = customers.reduce(
    (sum: number, customer: any) => sum + (customer.urgentOrdersCount || 0),
    0
  );

  return {
    totalOrders,
    totalCustomers: customers.length,
    totalValues,
    urgentOrders,
  };
}
